import { create } from "zustand";
import { isSEmbedTheme, type SEmbedTheme } from "@norma-bi/s-embed-sdk";

export type ThemeChoice = SEmbedTheme;

const STORAGE_KEY = "s-embed-theme";
const systemDark = window.matchMedia("(prefers-color-scheme: dark)");

// A per-device preference, kept across sign-outs. Until the user picks one, BI follows the device.
// `index.html` applies the same rule before the first paint.
const DEFAULT_THEME: ThemeChoice = "system";

function savedTheme(): ThemeChoice | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isSEmbedTheme(value) ? value : null;
  } catch {
    return null; // Blocked storage falls back to the default.
  }
}

/**
 * BI owns the theme: its own pages use it, and S gets it as `?theme` and `S_EMBED_THEME`. The user
 * picks it in S's profile menu, which only asks BI with `S_EMBED_THEME_CHANGE`.
 */
type ThemeStore = {
  theme: ThemeChoice;
  setTheme: (theme: ThemeChoice) => void;
};

export const useThemeStore = create<ThemeStore>()((set) => ({
  theme: savedTheme() ?? DEFAULT_THEME,
  setTheme: (theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // The theme still applies to this page.
    }
    set({ theme });
  },
}));

// Host pages follow S's theme through the `dark` class that the S tokens read.
function applyTheme() {
  const { theme } = useThemeStore.getState();
  const dark = theme === "dark" || (theme === "system" && systemDark.matches);
  document.documentElement.classList.toggle("dark", dark);
  // Native controls (checkboxes, autofill, scrollbars) follow this, not the OS.
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}
applyTheme();
useThemeStore.subscribe(applyTheme);
systemDark.addEventListener("change", applyTheme);
